import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { ukDate } from "./crmShared";
import { LEAD_STATUSES, LEAD_PRIORITIES } from "./CrmVendorLeads";
import { NotesPanel } from "./NotesPanel";
import { FollowUpControl } from "./FollowUpControl";
import { useStageMover } from "./useStageMover";
import { ReferralPanel } from "./ReferralPanel";
import { StageReviewBanner } from "./StageReviewBanner";
import { VENDOR_STAGES_ALL, stageTone } from "./crmPipelineShared";
import { SourceBadge } from "./SourceBadge";
import { RecordLifecycle } from "./RecordLifecycle";
import { LoadError, errMsg } from "./LoadError";

const priBadge = (p) => p === "Hot" ? "bg-orange-100 text-orange-800" : p === "Warm" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600";

const PD_LABELS = { property_type: "Property type", bedrooms: "Bedrooms", condition: "Condition", occupancy: "Occupancy",
  on_market: "On market", asking_price: "Asking price", price_expectation: "Price expectation", reason: "Reason for selling",
  timeline: "Timeline", heard: "Heard via", is_owner: "Is owner" };

export default function CrmVendorLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [hasReferral, setHasReferral] = useState(false);
  const [refKey, setRefKey] = useState(0);
  const onRefsLoaded = useCallback((refs) => setHasReferral(refs.some((r) => !r.voided && r.vendor_store === "lead" && r.vendor_id === id)), [id]);

  const load = useCallback(async () => {
    setLoadErr("");
    try { const { data } = await api.get(`/crm/vendor-leads/${id}`); setD(data); }
    catch (e) { if (e?.response?.status === 404) setNotFound(true); else setLoadErr(errMsg(e, "Could not load vendor lead")); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (field, value) => {
    try { await api.patch(`/leads/${id}`, { [field]: value }); toast.success(`${field.replace(/_/g, " ")} updated`); load(); }
    catch (e) { toast.error(errMsg(e, "Update failed")); }
  };
  const mover = useStageMover({ view: "vendor", onMoved: () => { load(); setRefKey((k) => k + 1); } });
  const setFollowup = async (date) => {
    try { await api.post(`/crm/unified-contacts/lead/${id}/followup`, { date }); toast.success(date ? "Follow-up updated" : "Follow-up cleared"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not set follow-up")); }
  };
  const addActivity = async (action) => {
    const note = window.prompt(`${action} note (optional)`);
    if (note === null) return;
    const body = { action, note };
    try { await api.post(`/leads/${id}/activity`, body); toast.success("Logged"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not log activity")); }
  };

  if (notFound) return (
    <CrmShell title="Vendor Lead">
      <button onClick={() => navigate("/crm/vendor-leads")} className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to vendor leads</button>
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-8 text-center text-emerald-100/60" data-testid="vendor-not-found">This vendor lead could not be found.</div>
    </CrmShell>
  );
  if (loadErr && !d) return <CrmShell title="Vendor Lead"><LoadError message={loadErr} onRetry={load} testId="vendor-load-error" /></CrmShell>;
  if (!d) return <CrmShell title="Vendor Lead"><div className="text-emerald-100/50 text-sm py-8">Loading…</div></CrmShell>;

  const pd = d.property_details || {};
  const state = d.record_state || (d.is_deleted ? "archived" : "active");
  return (
    <CrmShell title="Vendor Lead">
      <button onClick={() => navigate("/crm/vendor-leads")} className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to vendor leads</button>
      {mover.dialog}
      <StageReviewBanner store="lead" id={id} review={d.crm_stage_review} onCleared={load} />
      {state !== "active" && <div className="mb-4"><RecordLifecycle store="lead" id={id} state={state} onDone={load} /></div>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><h1 className="text-2xl font-serif text-white" data-testid="vendor-detail-name">{d.full_name}</h1><div className="text-sm text-emerald-100/60 flex items-center gap-2 flex-wrap">{d.address || d.postcode || "—"} <SourceBadge source={d.original_source || d.source} testId="vendor-detail-source" /></div></div>
              <span className={`text-xs px-2.5 py-1 rounded-full ${priBadge(d.priority)}`} data-testid="vendor-detail-priority">{d.priority}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <a href={`tel:${d.phone}`} data-testid="vendor-detail-tel" className="flex items-center gap-1.5 text-emerald-100/80 hover:text-white"><Phone className="w-4 h-4 text-brass" />{d.phone || "—"}</a>
              <a href={`mailto:${d.email}`} data-testid="vendor-detail-mail" className="flex items-center gap-1.5 text-emerald-100/80 hover:text-white"><Mail className="w-4 h-4 text-brass" />{d.email || "—"}</a>
            </div>
          </div>

          <ReferralPanel store="lead" id={id} refreshKey={refKey} onLoaded={onRefsLoaded} />
          <NotesPanel recordType="lead" recordId={id} onAdded={load} readOnly={state !== "active"} />

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-2">Property details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              {Object.entries(PD_LABELS).filter(([k]) => pd[k]).map(([k, label]) => (
                <div key={k}><div className="text-emerald-100/50 text-xs">{label}</div><div className="text-white break-words">{String(pd[k])}</div></div>
              ))}
              <div><div className="text-emerald-100/50 text-xs">Expected price</div><div className="text-white">{d.expected_price || "—"}</div></div>
            </div>
            {d.notes && <div className="mt-3"><div className="text-emerald-100/50 text-xs">Notes</div><p className="text-white text-sm whitespace-pre-wrap">{d.notes}</p></div>}
            {d.linked_property && (
              <button onClick={() => navigate(`/crm/properties/${d.linked_property.id}`)} data-testid="vendor-linked-property" className="mt-4 flex items-center gap-1.5 text-sm text-brass hover:underline"><Building2 className="w-4 h-4" /> Open linked CRM property ({d.linked_property.deal_id})</button>
            )}
          </div>

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="vendor-activity">
            <h3 className="text-sm font-semibold text-white mb-3">Activity</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {[["Called", false], ["Emailed", false], ["Viewed", false]].map(([a]) => (
                <button key={a} onClick={() => addActivity(a, false)} data-testid={`vendor-add-${a.toLowerCase()}`} className="text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-1.5">+ {a}</button>
              ))}
            </div>
            <ol className="space-y-2.5">{(d.activity_history || []).map((a) => (
              <li key={a.id} className="flex gap-3 text-sm"><div className="w-2 h-2 rounded-full bg-brass mt-1.5 shrink-0" /><div><div className="text-white">{a.action}{a.note ? ` — ${a.note}` : ""}</div><div className="text-[11px] text-emerald-100/40">{new Date(a.created_at).toLocaleString("en-GB")} · {a.actor}</div></div></li>
            ))}</ol>
          </div>
        </div>

        <div className="space-y-5 lg:sticky lg:top-20 self-start" data-testid="vendor-side-panel">
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white">Stage & follow-up</h3>
            <div><label className="text-xs text-emerald-100/50">Referral pipeline stage</label>
              <div className={`mt-1 mb-1.5 inline-block text-[11px] px-2 py-0.5 rounded-full border ${stageTone(d.crm_pipeline_stage || "New Enquiry")}`} data-testid="vendor-stage-badge">{d.crm_pipeline_stage || "New Enquiry"}</div>
              <select value={VENDOR_STAGES_ALL.includes(d.crm_pipeline_stage) ? d.crm_pipeline_stage : "New Enquiry"} disabled={state !== "active"}
                onChange={(e) => mover.request({ store: "lead", id, hasReferral }, e.target.value)} data-testid="vendor-stage-select"
                className="w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50">{VENDOR_STAGES_ALL.map((s) => <option key={s}>{s}</option>)}</select>
              {["Lost", "Not Interested", "Withdrawn"].includes(d.crm_pipeline_stage) && d.crm_lost_reason && <div className="text-[11px] text-red-300/80 mt-1">Reason: {d.crm_lost_reason}</div>}
            </div>
            <div><label className="text-xs text-emerald-100/50">Lead status (legacy)</label>
              <select value={d.status} onChange={(e) => patch("status", e.target.value)} data-testid="vendor-status-select" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <div><label className="text-xs text-emerald-100/50">Priority</label>
              <select value={d.priority} onChange={(e) => patch("priority", e.target.value)} data-testid="vendor-priority-select" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{LEAD_PRIORITIES.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <div data-testid="vendor-followup">
              <label className="text-xs text-emerald-100/50">Follow-up</label>
              <div className="mt-1"><FollowUpControl value={d.next_follow_up_date} onSet={setFollowup} testIdPrefix="vendor-followup" /></div>
            </div>
            {state === "active" && <RecordLifecycle store="lead" id={id} state={state} onDone={(m) => (m === "restore" ? load() : navigate("/crm/vendor-leads"))} />}
          </div>
        </div>
      </div>
    </CrmShell>
  );
}
